/**
 * How far a market order may move against the trader before it is refused.
 *
 * Today a market order carries no bound at all: a perp buy sends
 * `acceptablePrice = MaxUint256` and a spot buy `minAmountOut = 0`, i.e. "fill
 * me at any price". On a thin book that is an invitation. A tolerance turns
 * that into a price the trader has agreed to.
 *
 * Kept in integer basis points — 1% is 100, not 0.01 — so a tolerance never
 * carries float error into a bound that decides whether an order fills.
 */
import { dec } from "./decimal";

export const SLIPPAGE_PRESETS_BPS = [50, 100, 300] as const;
export const DEFAULT_SLIPPAGE_BPS = 100;
/** 0.01% is the finest the encoding carries; 50% is past any honest market. */
export const MIN_SLIPPAGE_BPS = 1;
export const MAX_SLIPPAGE_BPS = 5000;
/** Above this the control warns: it is a tolerance, not a target. */
export const HIGH_SLIPPAGE_WARN_BPS = 500;
/** Below this the bound is tighter than a typical spread; the order may not fill. */
export const LOW_SLIPPAGE_WARN_BPS = 5;

const BPS = BigInt(10000);

/**
 * A stored or typed tolerance made safe. Anything unusable becomes the default
 * — including absent and empty, which `Number()` would otherwise read as 0 and
 * clamp to the tightest possible bound.
 */
export const clampSlippageBps = (value: unknown): number => {
  if (value === null || value === undefined || value === "") {
    return DEFAULT_SLIPPAGE_BPS;
  }
  const parsed = Math.round(Number(value));
  if (!Number.isFinite(parsed)) return DEFAULT_SLIPPAGE_BPS;
  return Math.min(MAX_SLIPPAGE_BPS, Math.max(MIN_SLIPPAGE_BPS, parsed));
};

const floorToStepUnits = (value: bigint, step?: bigint): bigint =>
  step && step > BigInt(0) ? (value / step) * step : value;

const ceilToStepUnits = (value: bigint, step?: bigint): bigint => {
  if (!step || step <= BigInt(0)) return value;
  const remainder = value % step;
  return remainder === BigInt(0) ? value : value + (step - remainder);
};

/**
 * The worst price a perp market order may fill at, in the contract's 1e18 units.
 *
 * A buy may pay up to `ref × (1 + s)`, floored onto the price grid so the bound
 * itself is a legal price; a sell may receive down to `ref × (1 - s)`, ceiled
 * for the same reason. Rounding the bound toward the trader would loosen the
 * protection they asked for, so both go the strict way.
 */
export const perpAcceptablePrice = ({
  isLong,
  refPriceRaw,
  bps,
  priceStepRaw,
}: {
  isLong: boolean;
  refPriceRaw: bigint;
  bps: number;
  priceStepRaw?: bigint;
}): bigint => {
  const tolerance = BigInt(clampSlippageBps(bps));
  if (refPriceRaw <= BigInt(0)) return refPriceRaw;
  if (isLong) {
    return floorToStepUnits((refPriceRaw * (BPS + tolerance)) / BPS, priceStepRaw);
  }
  const bound = ceilToStepUnits((refPriceRaw * (BPS - tolerance)) / BPS, priceStepRaw);
  // The contract reads exactly 0 as "no bound" on either side, so a bound that
  // rounds to nothing must become the smallest real bound instead.
  return bound > BigInt(0) ? bound : BigInt(1);
};

/** The most quote a spot market BUY may spend: the estimate plus the tolerance. */
export const spotMaxQuoteIn = (estimatedQuoteRaw: bigint, bps: number): bigint => {
  const tolerance = BigInt(clampSlippageBps(bps));
  const bound = (estimatedQuoteRaw * (BPS + tolerance)) / BPS;
  // Ceil: a cap a wei under the true cost would refuse the order it is meant
  // to allow.
  return bound * BPS < estimatedQuoteRaw * (BPS + tolerance) ? bound + BigInt(1) : bound;
};

/**
 * The least a spot market order may receive, in the asset it RECEIVES: base
 * for a BUY, quote for a SELL (probed on Arc — passing the other asset, or the
 * gross figure, reverts every order with `SlippageTooHigh`). The estimate minus
 * the tolerance, then minus the taker fee, which is charged on the received side.
 */
export const spotMinAmountOut = (
  estimatedReceivedRaw: bigint,
  bps: number,
  takerFeeBps = 0
): bigint => {
  const tolerance = BigInt(clampSlippageBps(bps));
  const fee = BigInt(Math.max(0, Math.round(takerFeeBps)));
  const afterSlippage = (estimatedReceivedRaw * (BPS - tolerance)) / BPS;
  // Floor: asking for more than the fill can deliver reverts a good order.
  return (afterSlippage * (BPS - fee)) / BPS;
};

/** The tolerance as a percentage for display: 50 -> "0.5". */
export const slippagePercentText = (bps: number): string =>
  dec.parse(dec.div(String(clampSlippageBps(bps)), "100", 2, "floor") ?? "0") ?? "0";

/**
 * The worst price a market order would accept, for DISPLAY, from a reference
 * price in display units.
 *
 * This is what the trader is actually agreeing to, and until now nothing on
 * screen said it: the control showed "1%" and the bound it implied was
 * computed at submit and rendered nowhere. A trader only learned the number
 * from a revert message.
 *
 * It is an ESTIMATE and must be presented as one. The bound that gets signed
 * is built from an on-chain touch read at submit time (`readPerpTouch`),
 * deliberately, because the websocket book this reference comes from can be
 * stale. Same maths as `perpAcceptablePrice`, on display decimals rather than
 * raw integers — that one stays the submit path and is not changed here.
 *
 * Rounded AWAY from the trader (up for a buy, down for a sell) so the figure
 * is never kinder than the bound itself. `null` when the reference is not a
 * price anyone could fill at, so the caller shows unknown rather than a zero.
 */
export const worstFillPrice = (
  referencePrice: string | number | null | undefined,
  bps: number,
  isBuy: boolean,
  decimals = 2
): string | null => {
  const reference = dec.parse(referencePrice ?? null);
  if (reference === null || !dec.gt(reference, 0)) return null;

  const tolerance = clampSlippageBps(bps);
  const factor = dec.div(String(tolerance), String(BPS), 8, "floor");
  if (factor === null) return null;

  const multiplier = isBuy ? dec.add("1", factor) : dec.sub("1", factor);
  // A sell bound below zero is not a price. Cannot happen while the tolerance
  // is clamped to 50%, but the guard costs nothing and outlives the clamp.
  if (!dec.gt(multiplier, 0)) return null;

  const bound = dec.round(
    dec.mul(reference, multiplier),
    decimals,
    isBuy ? "ceil" : "floor"
  );
  // The guard above is on the REFERENCE; this one is on the RESULT. A sell
  // bound can floor to "0" at the market's own precision -- 0.004 at 1% is
  // 0.00396, which floors to 0 at 2dp -- and the callers format it as
  // currency, so the screen would read "$0.00": the confident zero this whole
  // branch exists to eliminate. Unknown is the honest answer.
  return dec.gt(bound, 0) ? bound : null;
};
